import { ref, computed, watch, onUnmounted, toValue } from "vue";
import {
	getBookingTimeConstraint,
	shouldShowCountdown,
	getCountdownConfig,
} from "../utils/bookingTimeConstraints.js";
import { useMasterDataHelpers } from "./useMasterDataHelpers.js";
import { walkInEndAfterEarlyEnd } from "../utils/walkInSelection.js";

/**
 * Student edit/delete time window + walk-in "Beenden" eligibility.
 * Timer runs only while `show` is true and the user is a student.
 *
 * @param {object} options
 * @param {import('vue').MaybeRefOrGetter<object|null>} options.booking
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.show
 */
export function useBookingModificationWindow(options) {
	const { userRole, modificationDeadlineSeconds } = useMasterDataHelpers();

	const nowTick = ref(new Date());
	const timeConstraint = ref(null);
	const editCountdownConfig = ref(null);

	const booking = computed(() => toValue(options.booking));
	const show = computed(() => toValue(options.show));

	const canEdit = computed(
		() => booking.value?.permissions?.canUpdate || false,
	);
	const canDelete = computed(
		() => booking.value?.permissions?.canDelete || false,
	);

	const updateTimeConstraint = () => {
		if (
			booking.value &&
			userRole.value === "student" &&
			(canEdit.value || canDelete.value)
		) {
			timeConstraint.value = getBookingTimeConstraint(
				booking.value,
				userRole.value,
				modificationDeadlineSeconds.value,
			);
		} else {
			timeConstraint.value = null;
		}
	};

	const updateEditCountdown = () => {
		if (!booking.value || userRole.value !== "student") {
			editCountdownConfig.value = null;
			return;
		}

		editCountdownConfig.value = getCountdownConfig(
			booking.value,
			userRole.value,
			modificationDeadlineSeconds.value,
			900, // 15 min warning advance
		);
	};

	const tick = () => {
		updateTimeConstraint();
		updateEditCountdown();
		nowTick.value = new Date();
	};

	let constraintInterval;

	const stopTimer = () => {
		if (constraintInterval) {
			clearInterval(constraintInterval);
			constraintInterval = undefined;
		}
	};

	const startTimer = () => {
		stopTimer();
		tick();
		constraintInterval = setInterval(tick, 1000);
	};

	watch(
		() => show.value && userRole.value === "student",
		(shouldRun) => {
			if (shouldRun) {
				startTimer();
			} else {
				stopTimer();
			}
		},
		{ immediate: true },
	);

	watch(
		booking,
		() => {
			updateTimeConstraint();
			updateEditCountdown();
		},
		{ immediate: true, deep: true },
	);

	onUnmounted(stopTimer);

	const timeRestricted = computed(
		() => timeConstraint.value?.restricted || false,
	);
	const canEditWithTime = computed(
		() => canEdit.value && !timeRestricted.value,
	);
	const canDeleteWithTime = computed(
		() => canDelete.value && !timeRestricted.value,
	);

	// Student's own walk-in booking that is still running right now
	const isRunningOwnWalkIn = computed(() => {
		return (
			userRole.value === "student" &&
			!!booking.value?.walkIn &&
			!!booking.value?.permissions?.canUpdate &&
			!booking.value?.cancelledAt &&
			nowTick.value < new Date(booking.value?.endTime)
		);
	});

	// Same predicate the backend uses: would pressing "Beenden" shorten anything at all?
	// If the end time it would set is not before the booking's current end time, the booking
	// has either been ended early already or is in its last 30 minutes - either way it can
	// only run out now, so "Beenden" stays visible but disabled.
	const walkInEndNoLongerPossible = computed(() => {
		if (!isRunningOwnWalkIn.value) {
			return false;
		}

		const newEndTime = walkInEndAfterEarlyEnd(booking.value, nowTick.value);
		if (!newEndTime) {
			return false;
		}

		return newEndTime >= new Date(booking.value.endTime);
	});

	const canEndWalkIn = computed(
		() => isRunningOwnWalkIn.value && !walkInEndNoLongerPossible.value,
	);

	const shouldShowEditModeCountdown = (mode) => {
		const currentMode = toValue(mode);
		return (
			(currentMode === "view" ||
				currentMode === "edit" ||
				currentMode === "edit-series" ||
				currentMode === "edit-instance") &&
			userRole.value === "student" &&
			!!booking.value &&
			(canEdit.value || canDelete.value) &&
			shouldShowCountdown(
				booking.value,
				userRole.value,
				modificationDeadlineSeconds.value,
			)
		);
	};

	const handleEditCountdownExpired = () => {
		updateTimeConstraint();
		updateEditCountdown();
	};

	return {
		userRole,
		timeRestricted,
		canEditWithTime,
		canDeleteWithTime,
		canEndWalkIn,
		walkInEndNoLongerPossible,
		editCountdownConfig,
		shouldShowEditModeCountdown,
		handleEditCountdownExpired,
	};
}
